import React, { useCallback, useState } from "react";
import CustomSelect from "views/components/customSelect/customSelect";
import CardLaneV2 from "views/components/cardByLane/cardByLaneV2";
import { useQuery } from "@tanstack/react-query";
import queryKeys from "shared/utils/constants/queryKeys";
import { getAllThemes } from "services/publicLiveScores";
import { fetchBC } from "services/bowlingCenterService";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Chip from "@mui/material/Chip";

import CopyIcon from "shared/images/copy.svg";
import OpenIcon from "shared/images/external-link.svg";
import ArrowIcon from "shared/images/arrow-down.svg";
import styles from "./styles-url.module.scss";

const GENERAL_URL = "/live-scores?";
const playerPreview = {
  activePlayer: false,
  active_player: false,
  game: 1,
  lane: 1,
  playerHcp: 0,
  playerName: "Player 1",
  playerNumber: 1,
  scoreCompletedGames: 0,
  teamHcp: 0,
  teamName: "",
  pins: [],
  scores: [9, 39, 69, 99, 118, 127, 156, 175, 184, 212],
  speed: [],
  // prettier-ignore
  throws: ["9", "/", "X", "-", "X", "-", "X", "/", "X", "/", "7", "2", "X", "-", "X", "-", "9", "-", "X", "X", "8"],
};
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 155,
    },
    className: styles.selectMenu,
  },
};

const ThemesUrl = () => {
  const [values, setValues] = useState({
    bowlingCenter: null,
    lanes: [],
    noOfPlayers: 1,
    theme: null,
  });
  const [url, setUrl] = useState(GENERAL_URL);

  const updateUrl = useCallback(
    (newValues) => {
      const finalUrl = new URLSearchParams({
        uuid: values.bowlingCenter?.uuid || "",
        noOfPlayers: values.noOfPlayers || 1,
        lane: values.lanes.join("-") || +values.bowlingCenter?.lanes[0] || "",
        theme: values.theme?.theme_id || 1,
        ...newValues,
      });

      const sortedFinalUrl = new URLSearchParams(
        [...finalUrl.entries()].sort(),
      );
      setUrl(GENERAL_URL + sortedFinalUrl.toString());
    },
    [values],
  );

  // useQuery hook to get all themes
  const { loading: loadingThemes, data: allThemes = [] } = useQuery({
    queryKey: [queryKeys.themes.getAllThemes],
    queryFn: getAllThemes,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      setValues((prev) => ({ ...prev, theme: data[0] }));
      updateUrl({ theme: data[0].theme_id });
    },
  });
  // get all bowling centers
  const { data: bowlingCenters, loading: loadingBCs } = useQuery({
    queryKey: [queryKeys.bowlingCenter.all],
    queryFn: fetchBC,
    refetchOnWindowFocus: false,
  });

  const handleChangeLane = ({ target }) => {
    if (target.value.length < 5) {
      setValues((prev) => ({
        ...prev,
        lanes: target.value.sort(),
      }));
      updateUrl({ lane: target.value.sort().join("-") });
    }
  };

  if (loadingThemes || loadingBCs || !values.theme)
    return (
      <section className={styles.container}>
        <div className={styles.lines} />

        <div className={styles.wrapper}>LOADING</div>
      </section>
    );

  return (
    <section className={styles.container}>
      <div className={styles.lines} />

      <div className={styles.wrapper}>
        <section>
          <div className={styles.flex}>
            <div className={styles.inputsWrapper}>
              <label className={styles.labels} style={{ width: "100%" }}>
                Bowling center
                <Autocomplete
                  disablePortal
                  options={bowlingCenters?.map((bc) => ({
                    uuid: bc.uuid,
                    lanes: Array(bc.lanes)
                      .fill("")
                      .map((_, i) => i + 1),
                    label: bc.companyName,
                  }))}
                  sx={{
                    display: "block",
                    width: "100%",
                    "& input": {
                      boxSizing: "border-box",
                      bgcolor: "transparent",
                      border: "1px solid white",
                      color: "white",
                      padding: "11px 16px",
                      fontSize: 15,
                      borderRadius: 1,
                      marginTop: 1,
                      width: "100%",
                      boxShadow: "0px 1px 10px rgba(255, 255, 255, 0.25)",
                    },
                  }}
                  renderInput={(params) => (
                    <div ref={params.InputProps.ref}>
                      <input type="text" {...params.inputProps} />
                    </div>
                  )}
                  disableClearable
                  onChange={(e, { uuid, lanes }) => {
                    setValues((prev) => ({
                      ...prev,
                      lanes: [lanes[0]],
                      bowlingCenter: {
                        uuid,
                        lanes,
                      },
                    }));
                    updateUrl({ uuid });
                  }}
                  noOptionsText="Bowling center not found"
                  // renderOption={(props, option, { selected }) => (
                  //   <li
                  //     {...props}
                  //     style={{
                  //       backgroundColor: selected ? "#fafafa" : "#fff",
                  //       textAlign: "center",
                  //     }}
                  //   >
                  //     {option.label}
                  //   </li>
                  // )}
                />
              </label>
              <div className={styles.multiSelectWrapper}>
                <label className={styles.labels}>Lanes</label>
                <Select
                  disabled={!values.bowlingCenter}
                  multiple
                  value={values.lanes || []}
                  IconComponent={(props) => {
                    return (
                      <div {...props}>
                        <img src={ArrowIcon} alt="open icon" />
                      </div>
                    );
                  }}
                  onChange={handleChangeLane}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={value}
                          size="small"
                          variant="outlined"
                          sx={{
                            bgcolor: "rgba(255, 255, 255, 0.25)",
                            color: "white",
                          }}
                        />
                      ))}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                  sx={{
                    width: "100%",
                  }}
                  classes={{
                    root: styles.multiSelect,
                    select: styles.select,
                    icon: styles.selectIcon,
                  }}
                >
                  {values.bowlingCenter?.lanes.map((name) => (
                    <MenuItem
                      key={name}
                      value={name}
                      sx={{
                        margin: "2px 2px",
                        borderRadius: 0.5,
                      }}
                    >
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </div>
              <CustomSelect
                label="Max players per lane"
                options={Array(10)
                  .fill("")
                  .map((_, i) => ({
                    value: i + 1,
                    valueDisplay: i + 1,
                  }))}
                value={values.noOfPlayers}
                onSelect={(noOfPlayers) => {
                  setValues((prev) => ({
                    ...prev,
                    noOfPlayers,
                  }));
                  updateUrl({ noOfPlayers });
                }}
                className={styles.smallSelects}
              />
              <CustomSelect
                label="Theme"
                options={allThemes?.map((theme) => ({
                  value: theme.theme_id,
                  valueDisplay: theme.theme_name,
                }))}
                value={values.theme.theme_id}
                onSelect={(theme) => {
                  const newTheme = allThemes.find((t) => t.theme_id === theme);
                  setValues((prev) => ({
                    ...prev,
                    theme: newTheme,
                  }));
                  updateUrl({ theme: newTheme.theme_id });
                }}
                className={styles.fullWidth}
              />
            </div>

            <div className={styles.previewWrapper}>
              <label htmlFor="#" className={styles.labels}>
                Preview
              </label>
              <div
                className={styles.preview}
                style={{
                  backgroundColor:
                    values.theme?.livescores_settings.background.color ||
                    "#000",
                }}
              >
                <CardLaneV2
                  player={playerPreview}
                  theme={values.theme.scorecard_settings}
                />
              </div>
            </div>
          </div>

          <div className={styles.urlInputWrapper}>
            <label htmlFor="#">URL</label>
            <div className={styles.urlInput}>
              <span>{url}</span>
              <button
                disabled={
                  values.bowlingCenter?.uuid === "" || values.lanes.length === 0
                }
                onClick={() => {
                  navigator.clipboard.writeText(
                    `${window.location.protocol}//${window.location.host}${url}`,
                  );
                }}
              >
                <img src={CopyIcon} alt="copy icon" />
              </button>
              <a
                className={
                  values.bowlingCenter?.uuid === "" || values.lanes.length === 0
                    ? styles.linkDisabled
                    : null
                }
                href={url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={OpenIcon} alt="open icon" />
              </a>
            </div>
          </div>
        </section>

        <hr />

        <h2>Leaderboard</h2>

        <section>
          <div className={styles.flex}>
            <div>inputs</div>

            <div className={styles.preview}>preview</div>
          </div>

          <div>
            <label htmlFor="#">URL</label>
            <div>input url</div>
          </div>
        </section>
      </div>
    </section>
  );
};

export default ThemesUrl;
